import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Linkedin, Instagram, Youtube, MapPin, Mail, Phone } from "lucide-react";
import { LOGO_URL, CONTACT } from "../lib/constants";

export default function Footer() {
  return (
    <footer className="bg-[#0a2540] text-white/90 mt-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img src={LOGO_URL} alt="HSA" className="h-14 w-14 object-cover rounded-md ring-1 ring-white/15" />
              <div>
                <div className="font-serif-hsa text-lg font-bold text-white">HSA Service Hub</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#d4af37]">One Umbrella · Endless Possibilities</div>
              </div>
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              HSA Service Hub provides comprehensive financial, tax, insurance, and legal service
              coordination for individuals, families, and businesses across the United States.
            </p>
            <div className="flex items-center gap-3 mt-6">
              {[
                { Icon: Facebook, label: "facebook" },
                { Icon: Linkedin, label: "linkedin" },
                { Icon: Instagram, label: "instagram" },
                { Icon: Youtube, label: "youtube" },
              ].map(({ Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  data-testid={`footer-social-${label}`}
                  aria-label={label}
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center hover:bg-[#d4af37] hover:border-[#d4af37] hover:text-[#0a2540] transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-serif-hsa text-base font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                ["/", "Home"],
                ["/about", "About Us"],
                ["/blog", "Blog"],
                ["/contact", "Contact"],
                ["/contact", "Book Consultation"],
              ].map(([to, label]) => (
                <li key={label}>
                  <Link to={to} className="text-white/70 hover:text-[#d4af37] transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-serif-hsa text-base font-semibold mb-4">Services</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                ["/services/financial", "Financial Services"],
                ["/services/tax", "Tax Services"],
                ["/services/insurance", "Insurance Services"],
                ["/services/legal", "Legal Services"],
                ["/services/business", "Business Services"],
                ["/services", "All Services"],
              ].map(([to, label]) => (
                <li key={label}>
                  <Link to={to} className="text-white/70 hover:text-[#d4af37] transition-colors">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-serif-hsa text-base font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-[#d4af37]" />
                <span>{CONTACT.phone}</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-[#d4af37]" />
                <span>{CONTACT.email}</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#d4af37]" />
                <span>{CONTACT.address}</span>
              </li>
              <li className="text-white/60 pt-1">{CONTACT.hours}</li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-white/10 mt-12 pt-8 text-xs text-white/55 leading-relaxed">
          <p className="mb-4">
            <span className="text-white/80 font-semibold">Disclaimer:</span> HSA Service Hub provides service guidance and
            coordination. Financial, tax, insurance, and legal services may be provided through qualified professionals
            where required. Information on this website is for general informational purposes only and should not be
            considered financial, tax, insurance, or legal advice. Clients should consult appropriate licensed
            professionals for advice specific to their situation.
          </p>
          <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-white/10">
            <div>© {new Date().getFullYear()} HSA Service Hub. All rights reserved.</div>
            <div className="flex flex-wrap gap-5">
              <Link to="/contact" className="hover:text-[#d4af37]">Privacy Policy</Link>
              <Link to="/contact" className="hover:text-[#d4af37]">Terms & Conditions</Link>
              <Link to="/contact" className="hover:text-[#d4af37]">Disclaimer</Link>
              <Link to="/contact" className="hover:text-[#d4af37]">Accessibility</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
